/**
 * PAGE PUBLIQUE — /reunion/:mid/confirmer
 * Confirmation de présence à la rencontre obligatoire de rentrée (membres & parents).
 * 3 étapes : Détails → Vos informations → Réponse envoyée.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarBlank, Clock, MapPin, VideoCamera, CheckCircle, XCircle, CircleNotch, ArrowRight, ArrowLeft, ListChecks } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STEPS = ["Détails", "Vos informations", "Confirmation"];

function Stepper({ step }) {
  return (
    <div className="flex items-center gap-2 mb-8" data-testid="reunion-stepper">
      {STEPS.map((s, i) => (
        <div key={s} className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border ${i < step ? "bg-[#C9A961] border-[#C9A961] text-black" : i === step ? "border-[#D90429] text-white" : "border-white/20 text-white/40"}`}>{i < step ? "✓" : i + 1}</div>
          <span className={`text-xs uppercase tracking-widest hidden sm:inline ${i === step ? "text-white" : "text-white/40"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="w-6 sm:w-10 h-px bg-white/15" />}
        </div>
      ))}
    </div>
  );
}

function Details({ m, onNext }) {
  return (
    <div data-testid="reunion-details">
      <div className="text-xs uppercase tracking-[0.3em] text-[#C9A961]">{m.target_label || "Membres et parents"}</div>
      <h1 className="text-3xl sm:text-4xl font-bold mt-2 leading-tight">{m.title}</h1>
      {m.description && <p className="text-white/60 mt-3">{m.description}</p>}
      <div className="grid sm:grid-cols-2 gap-3 mt-6 text-sm">
        <div className="flex items-center gap-3 border border-white/10 p-3"><CalendarBlank size={20} className="text-[#C9A961]" /><span data-testid="reunion-date">{m.date || "Date à confirmer"}</span></div>
        <div className="flex items-center gap-3 border border-white/10 p-3"><Clock size={20} className="text-[#C9A961]" /><span>{m.time || "Heure à confirmer"}{m.duration_min ? ` · ${m.duration_min} min` : ""}</span></div>
        <div className="flex items-center gap-3 border border-white/10 p-3 sm:col-span-2"><MapPin size={20} className="text-[#C9A961]" /><span>{m.location}</span></div>
        {m.meeting_link && <a href={m.meeting_link} target="_blank" rel="noreferrer" className="flex items-center gap-3 border border-white/10 p-3 sm:col-span-2 hover:border-[#C9A961]"><VideoCamera size={20} className="text-[#C9A961]" /><span className="truncate">{m.meeting_link}</span></a>}
      </div>
      <div className="mt-6">
        <div className="flex items-center gap-2 text-sm text-white/70"><ListChecks size={18} /> Ordre du jour</div>
        <ol className="mt-2 space-y-1 text-sm text-white/80 list-decimal list-inside" data-testid="reunion-agenda">
          {(m.agenda || []).map((a, i) => <li key={i}>{a}</li>)}
        </ol>
      </div>
      <button onClick={onNext} data-testid="reunion-next-btn" className="mt-8 w-full bg-[#D90429] text-white py-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-white hover:text-[#D90429] transition-colors">
        Confirmer ma présence <ArrowRight size={18} />
      </button>
    </div>
  );
}

const inputCls = "mt-1 w-full bg-white/5 border border-white/15 px-3 py-2.5 text-sm text-white outline-none focus:border-[#D90429]";

function Form({ mid, onBack, onDone }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", students: "", status: "confirme", attendees: 2, question: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/reunion/${mid}/confirmer`, { ...f, attendees: Number(f.attendees) || 1 });
      onDone(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue, réessaie.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reunion-form">
      <h2 className="text-2xl font-bold">Vos informations</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <button type="button" onClick={() => setF({ ...f, status: "confirme" })} data-testid="reunion-status-yes" className={`p-4 border text-left ${f.status === "confirme" ? "border-[#C9A961] bg-[#C9A961]/10" : "border-white/15"}`}>
          <CheckCircle size={22} weight="fill" className="text-green-500" /><div className="font-bold mt-1">Je serai présent(e)</div><div className="text-xs text-white/50">Avec mon/mes enfant(s)</div>
        </button>
        <button type="button" onClick={() => setF({ ...f, status: "refuse" })} data-testid="reunion-status-no" className={`p-4 border text-left ${f.status === "refuse" ? "border-[#D90429] bg-[#D90429]/10" : "border-white/15"}`}>
          <XCircle size={22} weight="fill" className="text-[#D90429]" /><div className="font-bold mt-1">Je ne pourrai pas</div><div className="text-xs text-white/50">On vous enverra le résumé</div>
        </button>
      </div>
      <label className="block text-sm text-white/70">Nom complet (parent ou membre) *<input required value={f.name} onChange={set("name")} data-testid="reunion-name" className={inputCls} placeholder="Ex. Marie Tremblay" /></label>
      <label className="block text-sm text-white/70">Courriel *<input required type="email" value={f.email} onChange={set("email")} data-testid="reunion-email" className={inputCls} placeholder="courriel@exemple.com" /></label>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="block text-sm text-white/70">Téléphone<input value={f.phone} onChange={set("phone")} data-testid="reunion-phone" className={inputCls} placeholder="819 000-0000" /></label>
        {f.status === "confirme" && <label className="block text-sm text-white/70">Nombre de personnes<input type="number" min={1} max={10} value={f.attendees} onChange={set("attendees")} data-testid="reunion-attendees" className={inputCls} /></label>}
      </div>
      <label className="block text-sm text-white/70">Nom de l'élève / des élèves<input value={f.students} onChange={set("students")} data-testid="reunion-students" className={inputCls} placeholder="Ex. Léo et Emma Tremblay" /></label>
      <label className="block text-sm text-white/70">Question ou point à aborder (optionnel)<textarea rows={3} value={f.question} onChange={set("question")} data-testid="reunion-question" className={inputCls} placeholder="Horaire, équipement, paiement…" /></label>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onBack} data-testid="reunion-back-btn" className="px-4 py-3 border border-white/20 text-sm flex items-center gap-2 hover:border-white/50"><ArrowLeft size={16} /> Retour</button>
        <button type="submit" disabled={busy} data-testid="reunion-submit-btn" className="flex-1 bg-[#D90429] text-white py-3 text-sm font-bold flex items-center justify-center gap-2 hover:bg-white hover:text-[#D90429] transition-colors disabled:opacity-60">
          {busy && <CircleNotch size={18} className="animate-spin" />} Envoyer ma réponse
        </button>
      </div>
    </form>
  );
}

function Done({ res }) {
  const ok = res.status === "confirme";
  const m = res.meeting || {};
  return (
    <div className="text-center" data-testid="reunion-done">
      {ok ? <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto" /> : <XCircle size={64} weight="fill" className="text-[#C9A961] mx-auto" />}
      <h2 className="text-3xl font-bold mt-4">{ok ? "Présence confirmée !" : "Réponse enregistrée"}</h2>
      <p className="text-white/60 mt-2">{ok ? `On vous attend le ${m.date || "jour de la rencontre"}${m.time ? ` à ${m.time}` : ""} — ${m.location}.` : "Merci de nous avoir prévenus. Le résumé de la rencontre vous sera transmis."}</p>
      <div className="mt-8 border border-white/10 p-5 text-left text-sm">
        <div className="text-xs uppercase tracking-widest text-[#C9A961] mb-3">Prochaines étapes</div>
        <ol className="space-y-2 list-decimal list-inside text-white/80">
          <li>Un courriel de confirmation vous a été envoyé.</li>
          <li>Consultez votre <a href={res.member_link || "/membres"} className="underline text-white" data-testid="reunion-member-link">Centre membre</a> : horaire, dossier, paiements, équipements.</li>
          <li>Complétez les actions demandées avant la rencontre (documents, renouvellement).</li>
          {ok && m.meeting_link && <li>Lien de la rencontre : <a href={m.meeting_link} className="underline text-white" target="_blank" rel="noreferrer">{m.meeting_link}</a></li>}
        </ol>
      </div>
      <a href={res.member_link || "/membres"} className="mt-6 inline-block bg-[#C9A961] text-black px-6 py-3 text-sm font-bold hover:bg-white transition-colors" data-testid="reunion-goto-membres">Ouvrir mon Centre membre</a>
    </div>
  );
}

export default function ReunionConfirmer() {
  const { mid } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [res, setRes] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/reunion/${mid}`)
      .then(({ data }) => setMeeting(data.meeting))
      .catch((e) => setError(e?.response?.data?.detail || "Rencontre introuvable."));
  }, [mid]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-10">
        <div className="text-[11px] uppercase tracking-[0.35em] text-white/40 mb-6">Institut Impact Élite Martial · Rencontre de rentrée</div>
        {error ? (
          <div className="text-center py-10" data-testid="reunion-error"><XCircle size={48} className="text-[#D90429] mx-auto" /><p className="mt-4 text-white/70">{error}</p><a href="/" className="mt-6 inline-block underline text-sm">Retour à l'accueil</a></div>
        ) : !meeting ? (
          <div className="flex justify-center py-16" data-testid="reunion-loading"><CircleNotch size={32} className="animate-spin text-[#C9A961]" /></div>
        ) : (
          <>
            <Stepper step={step} />
            {step === 0 && <Details m={meeting} onNext={() => setStep(1)} />}
            {step === 1 && <Form mid={mid} onBack={() => setStep(0)} onDone={(r) => { setRes(r); setStep(2); }} />}
            {step === 2 && res && <Done res={res} />}
          </>
        )}
      </div>
    </div>
  );
}
